import _ from 'lodash';

import { E0円非表示フラグ, Eフェーズ種別 } from '@/openapi/enum';
import { CデータPlbscfItem, データListItemResponse, 勘定科目ListItemResponse } from '@/openapi/user';

import 決算期Utility, { 決算期 } from '@/classes/common/決算期Utility';
import AbstractPlbscfService, {
  PlbscfYearValue,
  PlbscfListItem,
  PlbscfTypeList,
  YearItemType,
  PartialCデータPlbscfItem,
  PlbscfItemValue,
} from '@/classes/service/AbstractPlbscfService';
import AbstractデータService, { ServiceType, 帳票データ } from '@/classes/service/AbstractデータService';
import {
  CALC_PROCESS_MAP,
  CONFIGS_CF計算書,
  CONFIGS_CF計算書_計算式,
  CONFIGS_FCF,
  CONFIGS_変動損益,
  CONFIGS_損益計算書,
  CONFIGS_貸借対照表,
  CONFIGS_運転資金,
  PlbscfListItemConfig,
  PlbscfType,
  ValueType,
  計算Type,
} from '@/master/Plbscf';

export default class 分析PlbscfService extends AbstractPlbscfService<PlbscfTypeList[]> {
  private static plbscfTypeLists: PlbscfTypeList[] = [];

  protected serviceType = ServiceType.分析Plbscf;

  /** 分析決算期 */
  private 分析決算期: 決算期;

  // ------------------------------------------------------------------
  // constructor
  // ------------------------------------------------------------------

  public constructor(eフェーズ種別: Eフェーズ種別 | null = null) {
    super(eフェーズ種別);

    // クラス変数の取得
    this.分析決算期 = 決算期Utility.get分析決算期(決算期Utility.過去決算期数);
  }

  // ------------------------------------------------------------------
  // public static methods
  // ------------------------------------------------------------------

  /**
   * 初期化
   *
   * ※帳票一括印刷 のボタン押下時、選択した帳票でループする前に本処理を実行する（別タブでデータが更新されている可能性があるため）
   * ※帳票一括印刷 の最中（ループ中）は、再計算させないために「this.plbscfTypeLists」を使用して帳票出力する
   */
  public static initialize(): void {
    this.plbscfTypeLists = [];
  }

  // ------------------------------------------------------------------
  // override
  // ------------------------------------------------------------------

  /**
   * @override
   * @param データs
   * @returns
   */
  public parse(データs: データListItemResponse[], option?: Record<string, true>): PlbscfTypeList[] {
    // 必要データに絞り込んで計算可能データ化
    const filteredデータs = this.フェーズ種別Filter(this.入力分類Filter(データs));
    const 表示勘定科目s = this.表示勘定科目s(this.勘定科目s);

    // DBのデータをtデータPlbscfsに設定、存在しない期は作成
    this.rawTデータPlbscfs = this.分析決算期.header.map((header) => {
      const foundデータ = filteredデータs.find((データ) => データ.c番号 === header.year);
      return {
        データpk: foundデータ?.pk,
        番号: header.year,
        items: foundデータ === undefined ? [] : (JSON.parse(foundデータ.cデータ) as CデータPlbscfItem[]),
      };
    });

    // CF計算書の場合、先頭列（5期前のデータ）を取り除く
    const sliceArrayIfCF計算書 = <T>(type: PlbscfType, array: T[]): T[] => {
      return type === PlbscfType.CF計算書 ? array.slice(1) : array;
    };

    // 計算用データを作成
    this.calcTypeLists = Object.keys(PlbscfType)
      .map(Number)
      .filter(Number.isInteger)
      .map((type) => {
        const header = sliceArrayIfCF計算書(type, this.分析決算期.header);
        const list: PlbscfListItem[] = this.createConfigListItems(表示勘定科目s, type).map((listItem) => ({
          ...listItem,
          yearValues: sliceArrayIfCF計算書(type, listItem.yearValues),
        }));
        return {
          type,
          header,
          list,
        };
      });

    // 計算結果を算出し、画面表示用データを加工して返却
    return this.calcAndCreatePlbscfTypeList(option?.isNoFilter);
  }

  /**
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse帳票ListItemRequest(
    データs: データListItemResponse[],
    option?: Record<string, string | number>
  ): 帳票データ {
    // 既に計算済の場合は再計算しない
    if (分析PlbscfService.plbscfTypeLists.length === 0) {
      分析PlbscfService.plbscfTypeLists = this.parse(データs);
    }

    const plbscfType = option!.plbscfType as PlbscfType;
    const typeList = 分析PlbscfService.plbscfTypeLists.find((p) => p.type === plbscfType)!;
    return this.createPlbscf帳票データ(plbscfType, typeList);
  }

  // ------------------------------------------------------------------
  // public methods
  // ------------------------------------------------------------------

  /**
   * クラス変数のcalcResultMapから指定計算Codeの値を取得
   *
   * @param itemValues
   * @param code
   * @param year
   * @param monthlyIndex
   * @param 部門pk
   * @returns
   */
  public getValueBy分析CalcResultMap(
    itemValues: PlbscfItemValue[],
    code: string,
    year: number,
    monthlyIndex?: number,
    部門pk?: number
  ): number {
    return super.getValueByCode(this.allCalcResultMap!, itemValues, code, year, monthlyIndex, 部門pk);
  }

  /**
   * 再計算し、画面表示用データを生成
   *
   * @param isNoFilter
   * @returns
   */
  public calcAndCreatePlbscfTypeList(isNoFilter?: true): PlbscfTypeList[] {
    // 計算結果を算出し、画面表示用データに設定
    this.setValuesByCalcResult();

    // 画面表示用データを加工して返却
    return this.createPlbscfTypeList(isNoFilter);
  }

  /**
   * 画面表示用データを生成
   *
   * @param isNoFilter
   * @returns
   */
  public createPlbscfTypeList(isNoFilter?: true): PlbscfTypeList[] {
    if (isNoFilter) return this.calcTypeLists;

    return this.calcTypeLists.map((t) => {
      let list =
        AbstractデータService.計画書.c分析0円非表示フラグ === E0円非表示フラグ.表示
          ? t.list
          : this.filterNotZero(t.list);
      if (t.type === PlbscfType.損益計算書) list = this.filter製造原価科目未使用(list);
      if (t.type === PlbscfType.FCF) list = this.filterFCF種別(list);
      return {
        ...t,
        list,
      };
    });
  }

  // ------------------------------------------------------------------
  // private methods
  // ------------------------------------------------------------------

  /**
   * 非表示チェック
   * @param colIndex
   * @param blankColumns
   * @returns
   */
  private isBlank(colIndex: number, blankColumns: number[] | undefined): boolean {
    return blankColumns !== undefined && blankColumns.includes(colIndex);
  }

  /**
   * 画面表示用データ作成（config情報から生成、valuesは初期値0）
   * @param 勘定科目s
   * @param type
   * @returns
   */
  private createConfigListItems(勘定科目s: 勘定科目ListItemResponse[], type: PlbscfType): PlbscfListItem[] {
    // データ生成用
    let configs: PlbscfListItemConfig[] = [];
    switch (type) {
      case PlbscfType.貸借対照表:
        configs = _.cloneDeep(CONFIGS_貸借対照表);
        break;
      case PlbscfType.損益計算書:
        configs = _.cloneDeep(CONFIGS_損益計算書);
        break;
      case PlbscfType.CF計算書:
        configs = _.cloneDeep(CONFIGS_CF計算書);
        break;
      case PlbscfType.CF計算書_計算式:
        configs = _.cloneDeep(CONFIGS_CF計算書_計算式);
        break;
      case PlbscfType.変動損益:
        configs = _.cloneDeep(CONFIGS_変動損益);
        break;
      case PlbscfType.運転資金:
        configs = _.cloneDeep(CONFIGS_運転資金);
        break;
      case PlbscfType.FCF:
        configs = _.cloneDeep(CONFIGS_FCF);
        break;
      default:
        return [];
    }

    // 返却値
    const list: PlbscfListItem[] = [];
    // データ生成用定数からデータを生成
    for (const config of configs) {
      switch (config.valueType) {
        case ValueType.noValue:
          list.push({
            config: _.cloneDeep(config),
            yearValues: this.rawTデータPlbscfs.map((tデータPlbscf, i) => ({
              year: tデータPlbscf.番号,
              item: {},
              type: this.isBlank(i, config.blankColumns) ? YearItemType.blank : undefined,
            })),
          });
          break;
        case ValueType.勘定科目:
          // 勘定科目行を追加
          this.add勘定科目ListItems(list, 勘定科目s, config);
          break;
        default:
          // 計算Code行を追加
          this.addCodeListItem(list, 勘定科目s, config);
          break;
      }
    }
    return list;
  }

  /**
   * 勘定科目行を追加
   * @param list
   * @param 勘定科目s
   * @param config
   */
  private add勘定科目ListItems(
    list: PlbscfListItem[],
    勘定科目s: 勘定科目ListItemResponse[],
    config: PlbscfListItemConfig
  ): void {
    // filterの型チェック
    if (!this.is勘定科目Filter(config.valueType, config.filter)) return;

    // 検索条件で勘定科目をフィルタリングし、紐づくデータ値を設定する
    const filtered勘定科目s = this.filtered勘定科目s(勘定科目s, config.filter);
    for (const 勘定科目 of filtered勘定科目s) {
      const yearValues: PlbscfYearValue[] = [];
      for (let i = 0; i < this.rawTデータPlbscfs.length; i++) {
        const tデータPlbscf = this.rawTデータPlbscfs[i];
        let foundItem = tデータPlbscf.items.find((item) => item.勘定科目Pk === 勘定科目.pk);
        if (foundItem === undefined) {
          // tデータに存在しないなら追加
          foundItem = {
            勘定科目Pk: 勘定科目.pk,
            value: 0,
          };
          tデータPlbscf.items.push(foundItem);
        }
        yearValues.push({
          year: tデータPlbscf.番号,
          item: foundItem!,
          type: this.isBlank(i, config.blankColumns) ? YearItemType.blank : undefined,
        });
      }
      // 一行追加
      const row = {
        config: _.cloneDeep(config),
        yearValues,
        勘定科目,
      };
      row.config.label.name = 勘定科目.c勘定科目;
      list.push(row);
    }
  }

  /**
   * 計算Code行を追加
   * @param list
   * @param 勘定科目s
   * @param config
   */
  private addCodeListItem(
    list: PlbscfListItem[],
    勘定科目s: 勘定科目ListItemResponse[],
    config: PlbscfListItemConfig
  ): void {
    // filterの型チェック
    if (!this.isCodeFilter(config.valueType, config.filter)) return;
    const code = config.filter.code;
    if (!CALC_PROCESS_MAP.has(code)) return;
    // 計算Codeに紐づくCalcProcessを取得
    const calcProcessConfig = CALC_PROCESS_MAP.get(code)!.config;
    // 表示が必須でない かつ 勘定科目合計の場合、データの存在チェック
    if (!config.filter.required && calcProcessConfig.計算Type === 計算Type.勘定科目Sum) {
      const filtered勘定科目s = this.filtered勘定科目s(勘定科目s, calcProcessConfig.勘定科目Sum!);
      // 科目が存在しない場合は終了
      if (filtered勘定科目s.length === 0) return;
    }
    // 一行追加
    const yearValues: PlbscfYearValue[] = [];
    for (let i = 0; i < this.rawTデータPlbscfs.length; i++) {
      // 現在年のDBデータを取得（value：undefined可として扱う）
      const partialItems = this.rawTデータPlbscfs[i].items as PartialCデータPlbscfItem[];
      let foundItem: PartialCデータPlbscfItem | undefined = partialItems.find((item) => item.計算Code === code);
      if (foundItem === undefined) {
        // tデータに存在しないなら追加
        foundItem = {
          計算Code: code,
        };
        partialItems.push(foundItem);
      }
      yearValues.push({
        year: this.rawTデータPlbscfs[i].番号,
        item: foundItem!,
        calcValue: 0,
        type: this.isBlank(i, config.blankColumns) ? YearItemType.blank : undefined,
      });
    }
    list.push({
      config: _.cloneDeep(config),
      yearValues,
    });
  }
}
